import { inject, Injectable, signal } from '@angular/core';
import { AzureStorageService } from '../../infrastructure/azure-storage/azure-storage.service';
import {
  EditableViewSettingsGroup,
  ViewBasicData,
  ViewGroup,
  ViewSettings,
  ViewSummary,
} from '@aksia/models';
import { GridApi } from 'ag-grid-community';

@Injectable({
  providedIn: 'root',
})
export class DocumentManagerViewsService {
  private azureStorageService = inject(AzureStorageService);

  user: any;
  canEdit: boolean = false;
  containerName: string = 'document-manager-views';
  viewSettingsGroups = signal<EditableViewSettingsGroup[]>([]);
  viewGroups: ViewGroup[] = [
    {
      id: 1,
      name: 'Aksia',
      canEdit: true,
      default: false,
      parentGroupId: 2,
    },
    {
      id: 2,
      name: 'User',
      canEdit: true,
      default: true,
    },
  ];
  selectedView = signal<ViewSettings | undefined>(undefined);

  setUpConstants(user: any, canEdit: boolean) {
    this.user = user;
    this.canEdit = canEdit;
    this.viewGroups[0].canEdit = canEdit;
    this.viewGroups[1].canEdit = true;
  }

  async init(clientSideSettings: any): Promise<boolean> {
    return await this.azureStorageService?.init(
      clientSideSettings,
      this.containerName,
    );
  }

  setSelectedView(view: ViewSettings) {
    const group = this.viewSettingsGroups().find((g) => g.id === view.groupId);
    view.canEdit = group?.canEdit ?? false;
    this.selectedView.set(view);
  }

  async getUserViewSettings(
    userName: string,
  ): Promise<{ error: boolean | undefined }> {
    const userViews = await this.azureStorageService.downloadBlobAsText(
      this.containerName,
      userName,
    );
    const aksiaViews = await this.azureStorageService.downloadBlobAsText(
      this.containerName,
      this.viewGroups.find((g) => g.id === 1)!.name,
    );

    if (userViews === null && aksiaViews === null) return { error: true };

    const userViewsParsed = userViews ? JSON.parse(userViews) : [];
    const aksiaViewsParsed = aksiaViews ? JSON.parse(aksiaViews) : [];

    this.viewSettingsGroups.set([
      {
        id: 1,
        name: 'Aksia',
        canEdit: this.canEdit,
        views: aksiaViewsParsed,
        default: false,
        parentGroupId: 2,
      },
      {
        id: 2,
        name: 'User',
        canEdit: true,
        views: userViewsParsed,
        default: true,
      },
    ]);

    return { error: false };
  }

  getFirstView() {
    let firstView = this.viewSettingsGroups()
      .flatMap((group) => group.views)
      .find((view) => view?.isFavorite);
    if (firstView) return firstView;

    return this.viewSettingsGroups()
      ?.toReversed()
      ?.flatMap((group) => group.views)
      ?.find((view) => view?.isDefault);
  }

  async createView(newView: ViewBasicData, gridApi: any) {
    const groupName = this.getGroupNameByView(newView);
    let existingViews = this.getExistingViews(newView);

    const nameExists = existingViews.find((v) => v.name === newView.name);
    if (nameExists)
      return { success: false, message: 'View name already exists' };

    const newViewSettings: ViewSettings = {
      name: newView.name,
      columnState: gridApi.getColumnState(),
      filterModel: gridApi.getFilterModel(),
      createdOn: new Date().toLocaleString(),
      createdBy: this.user.name,
      modifiedOn: undefined,
      modifiedBy: undefined,
      isDefault: false,
      isFavorite: false,
      groupId: newView ? newView.groupId : 1,
      canEdit: true,
    };

    const viewSummary: ViewSummary = {
      name: groupName,
      views: JSON.stringify([...existingViews, newViewSettings]),
    };

    const result = await this.azureStorageService.uploadFile(
      this.containerName,
      viewSummary.name,
      viewSummary.views,
    );
    if (!result) return { success: false };

    existingViews.push(newViewSettings);
    this.selectedView.set(newViewSettings);
    this.viewSettingsGroups.update((groups) => [...groups!]);
    return { success: true };
  }

  async updateView(gridApi: GridApi<any>) {
    if (!this.selectedView()) return { success: false, message: undefined };

    const existingViews = this.getExistingViews();
    let existingView = existingViews.find(
      (v) => v.name === this.selectedView()!.name,
    );
    if (!existingView) return { success: false };

    existingView.columnState = gridApi.getColumnState();
    existingView.filterModel = gridApi.getFilterModel();
    existingView.modifiedBy = this.user.name;
    existingView.modifiedOn = new Date().toLocaleString();

    return await this._update(existingViews, existingView);
  }

  async editViewName(viewName: string | undefined) {
    if (!this.selectedView()) return { success: false };

    const existingViews = this.getExistingViews();
    let existingView = existingViews.find(
      (v) => v.name === this.selectedView()!.name,
    );
    if (!existingView) return { success: false };

    const nameExists =
      viewName && existingViews.find((v) => v.name === viewName);
    if (nameExists)
      return { success: false, message: 'View name already exists' };

    existingView.name = viewName!;
    existingView.modifiedBy = this.user.name;
    existingView.modifiedOn = new Date().toLocaleString();

    return await this._update(existingViews, existingView);
  }

  private async _update(existingViews: any[], existingView: any) {
    const groupName = this.getGroupNameByView();
    const viewSummary: ViewSummary = {
      name: groupName,
      views: JSON.stringify(existingViews),
    };
    const result = await this.azureStorageService.uploadFile(
      this.containerName,
      viewSummary.name,
      viewSummary.views,
    );
    this.selectedView.set(existingView);
    this.viewSettingsGroups.update((groups) => [...groups!]);
    return { success: true, message: undefined };
  }

  async deleteView() {
    const existingViews = this.getExistingViews();
    const index = existingViews.findIndex(
      (v) => v.name === this.selectedView()!.name,
    );
    if (index === -1) return { success: false };

    existingViews.splice(index, 1);

    const groupName = this.getGroupNameByView();
    const viewSummary: ViewSummary = {
      name: groupName,
      views: JSON.stringify(existingViews),
    };
    const result = await this.azureStorageService.uploadFile(
      this.containerName,
      viewSummary.name,
      viewSummary.views,
    );
    this.selectedView.set(undefined);
    this.viewSettingsGroups.update((groups) => [...groups!]);
    return { success: true };
  }

  async setFavoriteView(view: ViewSettings) {
    const favoriteValue = !view.isFavorite;
    this.viewSettingsGroups.update((groups) => {
      groups?.forEach((group) => {
        group?.views?.forEach((s) => (s.isFavorite = false));
      });
      return [...groups];
    });
    view.isFavorite = favoriteValue;
    view.modifiedBy = this.user.userId?.toString()!;
    view.modifiedOn = new Date().toLocaleString();

    await this.viewSettingsGroups().forEach((group) => {
      const groupName = this.getGroupNameByGroupId(group.id);
      const viewSummary: ViewSummary = {
        name: groupName,
        views: JSON.stringify(group.views),
      };
      const result = this.azureStorageService.uploadFile(
        this.containerName,
        viewSummary.name,
        viewSummary.views,
      );
    });

    return { success: true };
  }

  async setGroupDefault() {
    if (!this.selectedView()) return { success: false };

    const existingViews = this.getExistingViews();
    const index = existingViews.findIndex(
      (v) => v.name === this.selectedView()!.name,
    );
    if (index === -1) return { success: false };

    existingViews.forEach((v) => (v.isDefault = false));
    existingViews[index].isDefault = true;

    const groupName = this.getGroupNameByView();
    const viewSummary: ViewSummary = {
      name: groupName,
      views: JSON.stringify(existingViews),
    };
    const result = await this.azureStorageService.uploadFile(
      this.containerName,
      viewSummary.name,
      viewSummary.views,
    );

    return { success: true };
  }

  private getExistingViews(view?: any) {
    const _view = view ?? this.selectedView();
    return (
      this.viewSettingsGroups().find((g) => g.id === _view.groupId)!.views ?? []
    );
  }

  private getGroupNameByView(view?: any) {
    const _view = view ?? this.selectedView();
    const userView = this.viewGroups.find((g) => g.name === 'User');
    return _view.groupId === userView?.id
      ? this.user.name
      : this.viewGroups.find((g) => g.id === _view.groupId)!.name;
  }

  private getGroupNameByGroupId(groupId: number) {
    const userView = this.viewGroups.find((g) => g.name === 'User');
    return groupId === userView?.id
      ? this.user.name
      : this.viewGroups.find((g) => g.id === groupId)!.name;
  }
}
